<template>
  <section v-once class="single_page" v-if="this.$i18n.locale=='en'">
      <div class="content">
          <!-- Titres, sous-titres et introduction -->
          <h1>Understanding cartoradio</h1>
          <div class="content_text">
              <p>
                  Cartoradio is used in particular, to identify the location of<a href="#installations">radio antennas</a>, to obtain information on the services
                   they provide, and , for given locations, to view the results of electromagnetic<a href="#measures">field measurements</a>summarised on a<a href="#file">measurement sheet</a>.
              </p>
              <p>
                  To access the results, users enter an address, a postcode or the name of a town or city. They can also measure the distance between two points by
                   activating the<img src="@/assets/img/texte/tool-measure.png" class="symbole" />icon in the bottom right corner of the map. They then click on a first spot, then on a second one, to get the distance between them.
              </p>
              <p>
                  This document explains how to understand the information on the sites, the antennas, the services carried by these antennas and the field measurements shown on Cartoradio.              </p>
              <!-- Radio installation media -->
              <h2>The “site” notion</h2>
              <p>
                  Sites are infrastructure (buildings, pylons, water towers, etc.) on which antennas are installed using technical systems such as masts, pylons or mast arms.              </p>
              <p>
                  Cartoradio displays the sites on which one or more antennas with equivalent isotropically radiated power (EIRP) greater than 5 watts are located.
                   The sites on which antennas belonging to the Civil Aviation and the Ministries of Defence and the Interior are located are not displayed for
                   security reasons. Sites on which antennas, and more generally equipment used solely for reception, such as rake antennas and satellite dishes
                   are located are also not displayed as their installation is not subject to authorisation.
              </p>
              <p>
                  A group of sites is shown by the<img src="@/assets/img/texte/SUP.png" />symbol. This symbol features the number of sites present in a zone of which the surface area depends on the map scale.
              </p>
              <h2>The notion of "stations" carried by the sites</h2>
              <p>
                  All the radio equipment belonging to a same company or public administration located on the same site is called a station.
              </p>

              <p>
                  For a telecom operator, a station may, for example, include equipment for the transport of signals by radio-relay systems and 2G, 3G, 4G and 5G
                   cell phone equipment.
              </p>
              <h2>The notion of "services" carried by radio antennas</h2>
              <p>
                  Antennas are used to transport, broadcast, transmit and/or receive radio signals. Depending on the type of radio signal and its use,
                   categories of services carried by the antennas can be defined.
              </p>

              <p>
                  Cartoradio uses six service categories: cell phone, professional mobile radio or PMR networks (ambulance companies, taxi companies,...),
                   television broadcasting, radio broadcasting, transport of signals by radio-relay systems and "other installations".
              </p>
              <!--Radio installations -->
              <a name="installations"></a>
              <h2> Information available from Cartoradio on services, sites, stations and antennas </h2>
              <p>
                  The information comes from an ANFR database updated by the operators of public or private radio installations as part of the administrative
                   procedure provided for in Article L.43 of the French Postal and Electronic Communications Code.
              </p>
              <!--Radio installations -->
              <h3> 1. Information on the service categories carried by the antennas </h3>
              <p>
                  At each site location, the service categories carried by the antennas are shown on the map by the following symbols:
                  <img src="@/assets/img/texte/SUP_LEG_01.png" class="symbole" />
                  <img src="@/assets/img/texte/SUP_LEG_02.png" class="symbole" />
                  <img src="@/assets/img/texte/SUP_LEG_03.png" class="symbole" />
                  <img src="@/assets/img/texte/SUP_LEG_04.png" class="symbole" /> .
              </p>
              <ul>
                  <li>
                      Sites hosting antennas for<b> cell phone </b>services are shown by the <img src="@/assets/img/texte/SUP_LEG_01.png" class="symbole"/>symbol.
                       These are the cell phone antennas for GSM (2G), UMTS (3G), LTE (4G) and 5G.
                  </li>
                  <p style="text-align: center">
                      <img src="@/assets/img/texte/SUP_PHOTO_TELEPHONIE.jpg" class="photo"/>
                  </p>
                  <li>
                      Sites hosting antennas for<b> television broadcasting </b>services are shown by the <img src="@/assets/img/texte/SUP_LEG_02.png" class="symbole"/> symbol.
                  </li>
                  <p style="text-align: center">
                      <img src="@/assets/img/texte/SUP_PHOTO_TV.jpg" class="photo" />
                  </p>
                  <li>
                      Sites hosting antennas for<b> radio broadcasting </b>services (short or medium wave, FM or digital radio) are shown by the
                       <img src="@/assets/img/texte/SUP_LEG_03.png" class="symbole"/> symbol.
                  </li>
                  <p style="text-align: center">
                      <img src="@/assets/img/texte/SUP_PHOTO_RADIO.jpg" class="photo"/>
                  </p>
                  <li>
                      Sites hosting antennas for<b> PRM </b>services are shown by the <img src="@/assets/img/texte/6.png" class="symbole"/> symbol.
                  </li>
                  <p style="text-align: center">
                      <img src="@/assets/img/texte/SUP_PHOTO_PRM.jpg" class="photo"/>
                  </p>
                  <li>
                      Sites hosting antennas for the<b> transmission of radio-frequency (RF) signals </b>are shown by the <img src="@/assets/img/texte/5.png" class="symbole" /> symbol.
                  </li>
                  <p style="text-align: center">
                      <img src="@/assets/img/texte/SUP_PHOTO_RF.jpg" class="photo"/>
                  </p>
                  <li>
                    <b>Other installations</b>are shown by the <img src="@/assets/img/texte/SUP_LEG_04.png" class="symbole" /> symbol. They include weather radar, GSM-R, AIS.
                  </li>
                  <p style="text-align: center">
                      <img src="@/assets/img/texte/SUP_PHOTO_AUTRE_02.jpg" class="photo" />
                  </p>
              </ul>
              <!--Site information  -->
              <h3> 2. Site information  </h3>
              <p>
                  When a site is clicked, the following information appears:
              </p>
              <ul>
                    <li>
                        The site identification number (an administrative reference to be referred to in all correspondence with ANFR regarding the installation);
                    </li>
                    <li>
                        The site description;
                    </li>
                    <li>
                        The site address;
                    </li>
                    <li>
                        The names of the operators of the antennas attached to the sites and the transmitted service categories.
                    </li>
              </ul>
              <!--Station and antenna information -->
              <h3> 3. Station and antenna information </h3>
              <p>
                  From the site detail window, when an antenna operator service is clicked, the following information appears in a new window:
              </p>
              <ul style="list-style-type: '- '">
                <li>
                    At the top of the window, the operator station commissioning date and last modification date
                </li>
                <li>
                    In the body of the window
                    <ul>
                        <li>
                            The antenna height on its support from the ground;
                        </li>
                        <li>
                            The antenna identification number;
                        </li>
                        <li>
                            Its orientation (for example: 0° indicates North, 90° indicates East, 180° indicates South, etc.);
                        </li>
                        <li>
                            The transmitter(s) carried by the antenna -
                             <router-link to="/glossaire#abreviation">the list of abbreviations used for each type of transmitter is available here;</router-link>
                        </li>
                        <li>
                            The commissioning date of each transmitter;
                        </li>
                        <li>
                            The frequency band(s) used by each transmitter - expressed in kHz up to 27,500 kHz, in MHz from 27.5 to 10,000 MHz, in GHz above that value
                        </li>
                    </ul>
                </li>
              </ul>

              <p>
                  For telecom operators, the Cartoradio display can be configured to show all sites on the map, regardless of whether they are
                   operational or awaiting commissioning, or only operational sites hosting 2G, 3G, 4G and/or 5G technology.
                   For example, by selecting an operator, the 5G technology and the operational sites category,
                   Cartoradio users will only see the sites hosting the selected operator with antennas with at least one 5G transmitter in
                   service on the map.
              </p>
              <!--Field measurements -->
              <a name="measures"></a>
              <h2>Field measurements</h2>
              <p>
                  In accordance with Article L.34-9-1 of the French Post and Electronic Communications Code, the ANFR ensures that the measurement
                   results sent to it by COFRAC-accredited laboratories are made available to the public.
              </p>
              <p>
                  <a href="https://www.anfr.fr/controle-des-frequences/exposition-du-public-aux-ondes/la-mesure-de-champ/protocole-de-mesure/#menu2" target="_blank">
                   The measurements are made using Agency protocol </a> ANFR DR15 published in the Official Journal. This protocol is used to provide reliable
                    results obtained using up-to-date scientific, technical and standards-based principles. Beyond its regulatory nature,
                    it can be used to find out the exposure levels at a given spot.
              </p>
              <p>
                  ANFR regularly updates a database of electromagnetic field levels for an increasing number of measurement locations.
                  Anyone who wants to have a measurement taken fills in an application form on the mesures.anfr.fr website or downloads it
                   from www.service-public.fr.
              </p>
              <p>
                  Each measurement result is shown on Cartoradio by the symbols <img src="@/assets/img/texte/MES.png" /> for radio antenna
                   measurements and <img src="@/assets/img/texte/OBJ.png" /> for static communicating
                   object measurements. Clicking them displays the detailed measurement results for that spot.
              </p>
              <p>
                  Users can view the results for the different levels (obtained from broadband field measurements), using the cursor available in
                   the display menu. This cursor can be used to select a range of values and only display the spots on the map inside this range.
              </p>
              <p style="text-align: center">
                  <img src="@/assets/img/texte/MES_CURSEUR02.png" class="photo" />
              </p>
              <!--Measurement sheets-->
              <a name="file"></a>
              <h3> Measurement sheets </h3>
              <p>
                  A measurement record includes the measurement date and location, geographical indications for the transmitter location and the summary of the measurement results.
              </p>
              <p>
                  The electric field strength expressed in V/m is extrapolated for certain services such as cell phone services to the maximum transmitter power when selective frequency measurements are taken.
              </p>
              <!--Listings to the Cartoradio site-->
              <h1> Creating an account on Cartoradio</h1>
              <p>
                  The creation of an account on Cartoradio ("register" function) allows users to manage their maps and data download options.
              </p>
              <!--Card Management-->
              <h2> Map Management </h2>
              <p>
                  Users can save the active map parameters so that they can go back to it later by clicking the
                   <img src="@/assets/img/texte/save.png" class="symbole"/> symbol. The following are stored:
              </p>
              <ul>
                  <li> The map surface area (zoom level and centre); </li>
                  <li> The visible installation display criteria (cell phone services, etc.); </li>
                  <li> The measurement display criteria. </li>
              </ul>
              <p>
                  Users find their map “portfolio” every time they log in and can manage it as they see fit (creation, modification, deletion)
                   at the top left of the window.
              </p>
              <!--Download data-->
              <h2> Downloading data </h2>
              <p>
                  Users can export part of the information contained in the Cartoradio database in CSV format using the
                   <img src="@/assets/img/texte/export.png" class="symbole"/> symbol at the bottom right of the map.
                  The information relates to media and measurements.
              </p>
              <p>
                  The data export is made from a surface area which can be a département, a commune, or the active map surface area.
              </p>
          </div>
          <div class="content_footer">
              <div class="date_maj_texte">Last update on July 27, 2021 - © Agence Nationale des Fréquences</div>
          </div>
      </div>
  </section>

  <section v-once class="single_page" v-else>
    <div class="content">
        <!-- Titres, sous-titres et introduction -->
        <h1>Comprendre cartoradio</h1>
        <div class="content_text">
            <p>
                Cartoradio permet, en particulier, d'identifier l'emplacement d'
                <a href="#installations" title="Hormis celles de l'Aviation Civile et des ministères de la Défense et de l'Intérieur, et les installations de faibles puissances (moins de 5 watts) qui sont exonérées d'accord ANFR">antennes radioélectriques</a>, d'obtenir des informations sur les services qu'elles portent, et de connaître, pour des emplacements donnés, des résultats de
                <a href="#mesures">mesures de champ</a> électromagnétiques synthétisés dans une
                <a href="#fichemesure">fiche de mesures</a> .
            </p>
            <p>
                Pour accéder aux résultats, l'utilisateur renseigne une adresse, un code postal ou le nom d'une ville. Il peut également mesurer la distance entre deux points, en activant l'icône
                <img src="@/assets/img/texte/tool-measure.png"  class="symbole" /> , en bas à droite de la carte. Il suffit ensuite de cliquer sur un premier point, puis sur un deuxième, pour obtenir la distance les séparant.
            </p>
            <p>
                La présente fiche explique comment comprendre les informations sur les sites, les antennes, les services portés par ces antennes et les mesures de champ représentées sur Cartoradio.
            </p>
            <!-- Les sites d'installations radioélectriques -->
            <h2>La notion de «sites»</h2>
            <p>
                Les sites sont des infrastructures (immeubles, pylônes, châteaux d’eau, etc.) sur lesquelles sont implantées des antennes via des dispositifs techniques comme des mâts, des pylônets ou des bras de déport.
            </p>
            <p>
                Le site Cartoradio affiche les sites sur lesquels sont implantées une ou des antennes dont la puissance isotrope rayonnée équivalente (PIRE) est supérieure à 5 watts. Les sites sur lesquels sont implantées des antennes appartenant à l'Aviation Civile et aux ministères de la Défense et de l'Intérieur ne sont pas affichés pour des raisons de sécurité. Les sites sur lesquels sont implantées des antennes, et plus généralement des équipements, servant uniquement à la réception, comme les antennes râteau et les paraboles, ne sont pas non plus affichés, aucune autorisation n’étant nécessaire pour leur installation.
            </p>
            <p>
                Un ensemble de sites est représenté par le symbole
                <img src="@/assets/img/texte/SUP.png" /> .
                Sur ce symbole figure le nombre de sites présents dans une zone dont l'étendue est en fonction de l'échelle de la carte.
            </p>
            <!-- Les stations portées par les sites -->
            <h2>La notion de «stations» portées par les sites</h2>
            <p>
                L’ensemble des équipements radioélectriques d’une même entreprise ou d’une même administration implantés sur un même support est appelé station.
            </p>
            <p>
                Pour un opérateur télécom, une station peut, par exemple, comprendre des équipements de transport de signaux par faisceaux hertziens et des équipements de téléphonie mobile 2G, 3G, 4G et 5G.
            </p>
            <!-- Les supports d'installations radioélectriques -->
            <h2>La notion de «services» portés par les antennes radioélectriques</h2>
            <p>
                Les antennes permettent de transporter, de diffuser, d’émettre et/ou de recevoir des signaux radioélectriques. En fonction du type de signal radioélectrique et de son usage, des catégories de services portés par les antennes peuvent être définies.
            </p>
            <p>
                Six catégories de service sont distinguées dans Cartoradio : la téléphonie mobile, les réseaux de radio mobiles professionnels ou PMR en anglais (sociétés d’ambulances, de taxis,…), la diffusion de télévision, la diffusion de radio, le transport de signaux par faisceaux hertziens et les "autres installations".
            </p>
            <!-- Les installations radioélectriques -->
            <a name="installations"></a>
            <h2>Les informations disponibles dans Cartoradio sur les services, les sites, les stations et les antennes</h2>
            <p>
                Les informations proviennent d'une base de données de l'ANFR alimentée par les exploitants d'installations radioélectriques, publics ou privés, dans le cadre de la procédure administrative prévue par l'article L.43 du code des postes et communications électroniques.
            </p>
            <!-- Les installations radioélectriques -->
            <h3>1. Les informations sur les catégories de services portés par les antennes</h3>
            <p>
                A chaque emplacement de sites, les catégories de services portés par les antennes sont représentées sur la carte par les symboles suivants :
                <img src="@/assets/img/texte/5.png" class="symbole" />
                <img src="@/assets/img/texte/6.png" class="symbole" />
                <img src="@/assets/img/texte/SUP_LEG_01.png" class="symbole" />
                <img src="@/assets/img/texte/SUP_LEG_02.png" class="symbole" />
                <img src="@/assets/img/texte/SUP_LEG_03.png" class="symbole" />
                <img src="@/assets/img/texte/SUP_LEG_04.png" class="symbole" /> .
            </p>
            <ul>
                <li>
                    Les sites hébergeant des antennes pour le service de
                    <b>téléphonie mobile</b> sont représentés par le symbole
                    <img src="@/assets/img/texte/SUP_LEG_01.png" class="symbole" /> . Il s'agit des "antennes-relais" de téléphonie mobile pour le GSM (2G), l'UMTS (3G), le LTE (4G) et la 5G.
                </li>
                <p style="text-align:center">
                    <img src="@/assets/img/texte/SUP_PHOTO_TELEPHONIE.jpg" class="photo" />
                </p>
                <li>
                    Les sites hébergeant des antennes pour le service de
                    <b>diffusion de télévision</b> sont représentés par le symbole
                    <img src="@/assets/img/texte/SUP_LEG_02.png" class="symbole" /> .
                </li>
                <p style="text-align:center">
                    <img src="@/assets/img/texte/SUP_PHOTO_TV.jpg" class="photo" />
                </p>
                <li>
                    Les sites hébergeant des antennes pour le service de
                    <b>diffusion de radio</b> (ondes courtes ou moyennes, FM ou radio numérique) sont représentés par le symbole
                    <img src="@/assets/img/texte/SUP_LEG_03.png" class="symbole" /> .
                </li>
                <p style="text-align:center">
                    <img src="@/assets/img/texte/SUP_PHOTO_RADIO.jpg" class="photo" />
                </p>
                <li>
                    Les sites hébergeant des antennes pour le service de <b>PMR</b> sont représentés par le symbole
                    <img src="@/assets/img/texte/6.png" class="symbole" /> .
                </li>
                <p style="text-align:center">
                    <img src="@/assets/img/texte/SUP_PHOTO_AUTRE_01.jpg" class="photo" />
                </p>
                <li>
                    Les sites hébergeant des antennes pour le service de <b>transport de signaux par faisceaux hertziens (FH)</b> sont représentés par le symbole
                    <img src="@/assets/img/texte/5.png" class="symbole" /> .
                </li>
                <p style="text-align:center">
                    <img src="@/assets/img/texte/Autres_FH.jpg" class="photo" />
                </p>
                <li>
                    Les
                    <b>"autres installations"</b> sont représentées par le symbole
                    <img src="@/assets/img/texte/SUP_LEG_04.png" class="symbole" /> . Elles recouvrent notamment les radars météo, le GSM-R, les AIS.
                </li>
                <p style="text-align:center">
                    <img src="@/assets/img/texte/SUP_PHOTO_AUTRE_02.jpg" class="photo" />
                </p>
            </ul>
            <!-- Les supports des antennes -->
            <h3>2. Les informations sur les sites</h3>
            <p>
                En cliquant sur un site, les informations suivantes apparaissent:
            </p>
            <ul>
                <li>Le numéro d'identification du site (une référence administrative à rappeler dans toute correspondance avec l'ANFR touchant à cette installation) ;</li>
                <li>La description du site;</li>
                <li>L'adresse d'implantation du site;</li>
                <li>Le nom des exploitants des antennes rattachées au site et les catégories de services émis.</li>
            </ul>
            <!-- Les antennes -->
            <h3>3. Les informations sur les stations et les antennes</h3>
            <p>
                A partir de la fenêtre détaillant un site, en cliquant sur les services portés par chaque exploitant des antennes, les informations suivantes apparaissent dans une nouvelle fenêtre :
            </p>
            <p>
                - Dans l’en-tête de la fenêtre, la date de mise en service et la date de dernière modification de la station de l’exploitant
            </p>
            <p>
                - Dans le corps de la fenêtre
            </p>
            <ul>
                <li>La hauteur de l'antenne sur son support par rapport au sol;</li>
                <li>Le numéro d’identification de l’antenne ;</li>
                <li>Son orientation (par exemple: 0° indique le Nord, 90° indique l’Est, 180° indique le Sud,…);</li>
                <li>Le(s) émetteur(s) porté(s) par l’antenne - <router-link to="/glossaire#abreviation">la liste des abréviations utilisées pour chaque type d’émetteur est disponible ici</router-link>;</li>
                <li>La date de mise en service de chaque émetteur;</li>
                <li>La/les bande(s) de fréquences utilisée(s) par chaque émetteur exprimées en kHz jusqu'à 27 500 kHz, en MHz de 27,5 à 10 000 MHz, en GHz au-dessus.</li>
            </ul>
            <p>
                Pour les opérateurs télécoms, l’affichage de Cartoradio peut être configuré de manière à faire apparaitre sur la carte tous les sites, qu’ils soient en service ou en attente de mise en service, ou uniquement les sites en service hébergeant les technologies 2G, 3G, 4G et/ou 5G. Par exemple, en sélectionnant un opérateur, la technologie 5G et la catégorie sites en service, un utilisateur de Cartoradio verra s’afficher sur la carte uniquement les sites hébergeant l’opérateur sélectionné avec des antennes ayant au moins un émetteur 5G en service.
            </p>

            <!-- Les mesures de champ -->
            <a name="mesures"></a>
            <h2>Les mesures de champ</h2>
            <p>
                Conformément à l'article L.34-9-1 du code des postes et communications électroniques, l'ANFR assure la mise à disposition du public des résultats de mesures qui lui sont transmis par les laboratoires accrédités par le COFRAC.
            </p>
            <p>
                <a href="https://www.anfr.fr/controle-des-frequences/exposition-du-public-aux-ondes/la-mesure-de-champ/protocole-de-mesure/#menu2" target="_blank">Les mesures sont réalisées suivant le protocole de l'Agence</a> ANFR DR15 publié au journal officiel. Ce protocole permet d'obtenir des résultats fiables reposant sur des fondements scientifiques, techniques et normatifs à jour. Il peut être utilisé, au-delà de son caractère réglementaire, pour connaître le niveau d'exposition en un point donné.
            </p>
            <p>
                L'ANFR actualise régulièrement une base de données des niveaux de champ électromagnétiques, pour un nombre croissant de lieux de mesures. Chaque personne qui souhaite faire réaliser une mesure remplit un formulaire de demande sur le site mesures.anfr.fr ou le télécharge sur www.service-public.fr.
            </p>
            <p>
                Chaque résultat de mesure est représenté sur Cartoradio par les symboles
                <img src="@/assets/img/texte/MES.png" /> pour les mesures concernant les antennes radioélectriques et
                <img src="@/assets/img/texte/OBJ.png" /> pour les mesures concernant les objets communicants fixes.
                En cliquant dessus on fait apparaître le résultat détaillé des mesures à cet endroit.
            </p>
            <p>
                L'utilisateur a la possibilité de visualiser les résultats des différents niveaux (obtenus à partir de mesures de champ large bande), en utilisant le curseur disponible dans le menu affichage. Ce curseur permet de sélectionner un intervalle de valeurs, pour n'afficher sur la carte que les points compris dans cet intervalle.
            </p>
            <p style="text-align:center">
                <img src="@/assets/img/texte/MES_CURSEUR.png" class="photo" />
            </p>
            <!-- Les fiches de mesures -->
            <a name="fichemesure"></a>
            <h3>Les fiches de mesures</h3>
            <p>
                Une fiche de mesures comporte la date et le lieu des mesures, des indications géographiques sur l'emplacement des émetteurs, ainsi que le résultat synthétique des mesures.
            </p>
            <p>
                Le niveau de champ électrique exprimé en V/m est extrapolé pour certains services comme la téléphonie mobile à puissance maximale des émetteurs lorsque les mesures sélectives en fréquence sont réalisées.
            </p>
            <!-- Inscriptions au site Cartoradio -->
            <h1>Créer un compte sur Cartoradio</h1>
            <p>
                La création d’un compte sur Cartoradio (fonction «s’inscrire») permet à l'utilisateur une gestion personnalisée de ses cartes et des possibilités de téléchargement de données.
            </p>
            <!-- Gestion des cartes -->
            <h2>Gestion des cartes</h2>
            <p>
                L'utilisateur peut mémoriser les paramètres de la carte active pour pouvoir y revenir ultérieurement en cliquant sur le symbole <img src="@/assets/img/texte/save.png" class="symbole"/> . Sont mémorisés :
            </p>
            <ul>
                <li>L'emprise de la carte (niveau de zoom et centre) ;</li>
                <li>Les critères d'affichage des installations visibles (téléphonie mobile, etc.) ;</li>
                <li>Les critères d'affichage des mesures.</li>
            </ul>
            <p>
                L'utilisateur retrouve son "portefeuille" de cartes à chaque connexion et le gère à sa guise (création, modification, suppression) en haut à gauche de la fenêtre .
            </p>
            <!-- Téléchargement des données -->
            <h2>Téléchargement des données</h2>
            <p>
                L'utilisateur a la possibilité d'exporter au format CSV une partie des informations contenues dans la base Cartoradio à l'aide du symbole en bas à droite de la carte <img src="@/assets/img/texte/export.png" class="symbole"/>. Les informations concernent les supports et les mesures.
            </p>
            <p>
                L'export de données s'effectue à partir d'une emprise spatiale qui peut être un département, une commune, ou l'emprise de la carte active.
            </p>
        </div>
        <div class="content_footer">
            <div class="date_maj_texte">Dernière actualisation le 27 juillet 2021 - © Agence Nationale des Fréquences</div>
        </div>
    </div>
  </section>
</template>

<script>

export default {
  name: 'Comprendre',
  components: { },
  data() {
    return {
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
